import { CANOPY_WEB_ORIGIN } from './environmentUtils';

export const LANDING_PAGE_ROUTE = '/login';

export const SPARKRX_PDT_UID = '8dpNPSmSnyN3vxeBi4RtM9';
export const SLEEPIO_PDT_UID = 'BxgXBrgwswcX58wFNwhVfv';
export const DAYLIGHT_PDT_UID = 'DhZzR9fNJyNGMETp6Qgr6K';
export const DAYLIGHT_PLATGEN_PDT_UID = 'WcLGEey8ZMtnpoKBDrWH26';

// https://www.oreilly.com/library/view/regular-expressions-cookbook/9781449327453/ch04s01.html
// eslint-disable-next-line
export const EMAIL_VALIDATION_REGEX = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/i;

// RTX's internal authentication alias domain — can't receive mail.
// Only supports one domain today; can be extended to a list later if needed.
export const BLOCKED_SIGNUP_EMAIL_DOMAIN = 'adxuser.com';

// https://ihateregex.io/expr/phone/
export const PHONE_VALIDATION_REGEX = /^[+]?\(?\d{3}\)?[-\s.]?\d{3}[-\s.]?\d{4,6}$/i;

// Validates this format: MM/DD/YYYY
export const DOB_VALIDATION_REGEX = /(?:(09|04|06|11)(\/)(0[1-9]|[12]\d|30)(\/)((?:19|20)\d\d))|(?:(01|03|05|07|08|10|12)(\/)(0[1-9]|[12]\d|3[01])(\/)((?:19|20)\d\d))|(?:02(\/)(?:(?:(0[1-9]|1\d|2[0-8])(\/)((?:19|20)\d\d))|(?:(29)(\/)((?:(?:19|20)(?:04|08|12|16|20|24|28|32|36|40|44|48|52|56|60|64|68|72|76|80|84|88|92|96))|2000))))/; // eslint-disable-line

// Validates zipcodes in these formats: #####
export const ZIPCODE_VALIDATION_REGEX = /^(\d{5})$/;

/* Validates password with these rules:
 - 8-24 characters
 - Include letters, numbers, and special characters
 */
export const PASSWORD_VALIDATION_REGEX = /^(?=.*[A-Za-z])(?=.*\d)(?=.*[^A-Za-z\d]).{8,24}$/;

export const EN = 'EN';
export const ES = 'ES';

export const LANGUAGE_LIST = [
  { value: EN, label: 'English' },
  { value: ES, label: 'Español' },
];

export const MINIMUM_BIRTH_YEAR = 1900;

export const SINGULAR_SDK_KEY = 'bighealth_8b9fd55c';
export const SINGULAR_SDK_SECRET = 'c65265155b6d1a8de24c00a5570aad38';

export const EMPLOYER_ORG_SLUG_KEY = 'employer_org_slug';
export const REDIRECT_TO_SESSION_KEY = 'cap_redirect_to';

/**
 * Every address CAP will hand a finished Sleepio session to, in full.
 *
 * Mirrors `VALID_REDIRECT_TARGETS` in vercel-auth-functions `lib/redirectAllowlist.ts` — one
 * entry per scheme the Sleepio app registers. There is no package shared between the two
 * repos, so this is the second of two statements of the same set; change one and the other
 * has to move with it. Both repos' contract tests write the strings out in full so a
 * disagreement shows up as a diff rather than as a failed sign-in.
 *
 * **Exact match, not "starts with an allowed scheme."** A scheme check admits
 * `sleepio://anything?attacker=controlled`, and this is the value the one-time exchange token
 * gets attached to. The auth service checks its own copy, but it only sees the address that
 * goes into the email — on the phone-OTP path it is never asked at all, so for that path this
 * list is the only thing deciding where a member's session is delivered.
 */
/**
 * The one query parameter a handoff address may carry: the app's per-flow nonce.
 *
 * The app mints an unguessable value when it starts a sign-in, appends it to the address it asks
 * us to send it back to, and refuses any handoff that does not carry it back. That is what binds
 * a returning session to the flow that asked for it.
 *
 * CAP never *mints* or *checks* it — the binding lives entirely on the device, which compares an
 * arrival against the set that install generated. CAP does **read** it in one place: when the
 * address itself did not survive the trip, `resolvePostAuthRedirectTo` lifts the flat copy off our
 * own callback URL and re-attaches it to the default address, because a fallback without the nonce
 * is a dead end rather than a degraded handoff. (An earlier version of this comment said CAP never
 * reads it. That was wrong, and so was the matching claim in the auth service and in canopy's
 * ADR-0001 invariant 12.)
 *
 * Mirrors `HANDOFF_NONCE_PARAM` in vercel-auth-functions `lib/redirectAllowlist.ts`; both repos
 * write the name out so a disagreement shows up as a diff. **Optional by design** — an app build
 * that predates the nonce sends no query and stays legal, which it must, because an installed app
 * is never fully replaced.
 *
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 * THIS PATH IS FORWARD-ONLY. READ THIS BEFORE ROLLING CAP BACK.
 *
 * Canopy builds from PAT-1286 onwards **refuse any handoff carrying no nonce** — the check is
 * `matchesCapHandoffNonce`, and an absent value answers "no match" by design, at three separate
 * token readers. A CAP version that predates the change never emits `hn` at all.
 *
 * So rolling CAP back past the PAT-1286 change (or cutting a hotfix branch from before it, or
 * leaving an environment on an older deploy) **refuses every sign-in for every updated app**. Not
 * degraded: refused. The member lands on the activation screen each time.
 *
 * And it cannot be fixed from the app side quickly. The installed base is never fully replaced, so
 * there is no "roll the app back"; the remedy is a code change in canopy plus a release. At the
 * time of writing the over-the-air escape hatch is **not available in production**: `updates.url`
 * and `runtimeVersion: { policy: 'appVersion' }` are configured in `apps/sleepio-expo/app.config.js`,
 * but the `production` build profile in `eas.json` declares no `channel` (only `dev` and `stage` do),
 * so a published update has nothing to reach. Confirm that with whoever owns the release pipeline
 * before counting on an OTA push.
 *
 * If CAP genuinely has to go back:
 *   1. Do not. Roll forward — fix the defect on top of the PAT-1286 change instead.
 *   2. If it is unavoidable, the app change needed is to stop requiring the nonce, in
 *      `packages/app/features/sleepio-activation/capHandoffNonce.native.ts` and its three callers
 *      (`useAuthCallback.native.ts`, `app/(auth)/exchange-callback.tsx`, `app/(auth)/activate.tsx`).
 *      That reopens the hole PAT-1286 closed — a link carrying a token an attacker minted against
 *      their own account signs a member into it — so it is an incident-grade tradeoff, not a config
 *      flip.
 *   3. Wiring a `channel` onto the production profile would turn step 2 into an OTA push rather
 *      than an App Store release. Worth doing regardless of this ticket.
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 */
export const HANDOFF_NONCE_PARAM = 'hn';

export const CANOPY_POST_AUTH_CALLBACK_TARGETS = [
  'sleep-companion://exchange-callback',
  'sleepio://exchange-callback',
] as const;

/** The path the Canopy web app redeems an exchange token on, mirroring the native route name. */
const CANOPY_WEB_CALLBACK_PATH = '/exchange-callback';

/**
 * Where the Canopy **web** app may be handed a finished session — deliberately separate from the
 * list above.
 *
 * That list is a cross-repo contract: vercel-auth-functions holds a byte-identical copy in
 * `lib/redirectAllowlist.ts`, and both repos' tests write the strings out in full so a
 * disagreement shows up as a diff. Adding a web address to it would break that agreement for no
 * benefit, because the auth service is never asked about this one. It writes a redirect into a
 * magic-link email; the phone-OTP journey sends no email, so CAP's own check is the only thing
 * deciding where that session goes. Keeping the two lists apart says exactly that.
 *
 * Per environment rather than one fixed string, because the web app's origin differs across
 * dev, stage and production — and because a byte-exact allowlist cannot pattern-match a host.
 * The origin comes from `CANOPY_WEB_ORIGIN` in environmentUtils, which is the same envSwitch
 * table CORTEX_URL, VAF_URL and SUPABASE_URL use. An environment with no web deployment has an
 * empty entry there and gets an empty list here, which fails closed to the manual handoff
 * screen.
 *
 * Outside production the local dev origins are allowed too, so a deployed non-production CAP can
 * hand back to a web app running on the engineer's own machine — which is the only way to
 * exercise this without deploying Canopy web on every change. Production takes the table's
 * origin and nothing else: no localhost, no fallback.
 */
const LOCAL_CANOPY_WEB_ORIGINS = ['http://localhost:3000', 'http://localhost:3001'];

export const canopyWebPostAuthCallbackTargets = (): readonly string[] => {
  const deployEnv = process.env.REACT_APP_DEPLOY_ENV;
  // envSwitch is typed as returning a string, but it indexes its table with whatever
  // REACT_APP_DEPLOY_ENV holds — so an unset or unrecognised value yields undefined at runtime
  // and the declared type is a promise the lookup cannot keep. Guarded here rather than trusted.
  const tableOrigin = (CANOPY_WEB_ORIGIN as string | undefined)?.trim().replace(/\/+$/, '');

  const origins = tableOrigin ? [tableOrigin] : [];
  if (deployEnv !== 'prod' && deployEnv !== 'production') {
    // Filtered rather than appended wholesale: the table already names localhost for the
    // `local` environment, and a duplicate entry would make the list disagree with itself.
    origins.push(...LOCAL_CANOPY_WEB_ORIGINS.filter((origin) => !origins.includes(origin)));
  }

  return origins.map((origin) => `${origin}${CANOPY_WEB_CALLBACK_PATH}`);
};

/**
 * Where the app should be re-opened once authentication finishes — the default used when the
 * app supplied no address of its own. Named separately from the list above because a default
 * is a different thing from a permitted value.
 *
 * There is one address, not two. By the time CAP hands back to the app it is always
 * holding a short-lived `exchange_token`, whichever journey the member took, and
 * `exchange-callback` is the screen that redeems one.
 *
 * The app's other entry point, `authCallback`, is a different stage of the flow: it
 * receives the emailed link itself, carrying a Supabase verify URL in `_p`. Sent an
 * `exchange_token` instead, it has no `_p` to work with and bounces the member back to
 * the activation screen — so a returning member who did everything right lands on
 * sign-up. Choosing between the two addresses by guessing the member's intent is what
 * produced that (PAT-1286); the two constants this replaces encoded the guess.
 *
 * This value is a default, never a decision. It is used only when the app supplied no
 * address of its own, because the app is the only author of its own return address.
 *
 * **This constant is meant to be deleted.** It is a bridge, kept only because links
 * already sitting in members' inboxes were sent before the auth service learned to carry
 * the app's address, and those links stay valid for as long as their token lives. Three
 * things have to be true before it can go: the auth-service change is deployed, one token
 * lifetime has passed since then, and there is a real screen for the no-address case
 * (DAYL-1136 — today that path sets no handoff URL, so the "Open Sleepio" button has
 * nothing to open). Until DAYL-1136 exists, deleting this strands anyone who opened the
 * link on a desktop.
 */
export const CANOPY_POST_AUTH_CALLBACK = 'sleep-companion://exchange-callback';

/**
 * Stand-in for the `:org_slug` segment on the Sleepio login route.
 *
 * A login magic link carries no organisation — vercel-auth-functions builds that URL and
 * has none to put in it — but the route pattern requires the segment, and an empty one
 * leaves a `//` that matches nothing. The login path never reads the value, so any
 * non-empty string works; this is named so it can be grepped and so a Cortex-facing call
 * can assert it never receives it.
 */
export const LOGIN_ORG_SLUG_PLACEHOLDER = 'unknown';
export const ACCESS_CODE_SESSION_KEY = 'cap_access_code';
export const DEPENDENT_ACTIVATION_DATA_KEY = 'dependent_activation_data';

export type LineOfBusiness = 'wellness' | 'rx';

/**
 * Sleepio Activation Flow A/B Experiment
 *
 * - VARIANT_A: LineStep - horizontal progress bar with 3 lines
 * - VARIANT_B: ListStep - numbered list showing steps with durations
 */
export const VARIANT_A = 'variant-a';
export const VARIANT_B = 'variant-b';

export const ONBOARDING_STEPS = [
  { titleKey: 'onboarding.steps.verify_email', durationKey: 'onboarding.steps.duration_30_seconds' },
  { titleKey: 'onboarding.steps.confirm_coverage', durationKey: 'onboarding.steps.duration_1_minute' },
  { titleKey: 'onboarding.steps.download_sleepio', durationKey: 'onboarding.steps.duration_1_minute' },
];
